import React, { useState, useEffect, useRef } from 'react'
import { Button, Card, Modal, Form, Select, Input, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  paginationInit,
  formatMessageNew,
  initCurryingObj,
  downLoadFileBlob,
  initValueAndLabel,
  uniqueFun
} from '@/utils/common'
import { modalConfirm, modalError, messageModal } from '@/utils/modalUtils'
import { hasPermission } from '@/annotations/nike-react'
import {
  addData,
  updateData,
  downloadData,
  disableRecords,
  getTableData,
  queryProductByCode,
  getPlantList
} from '@/services/rfid/rfidConfig/specialSkuConfig'
import { getExceptionTypeList, findSpecialCodeAndType } from '@/services/rfid/rfidConfig/specialTypeConfig'
import SearchBar from '@/components/searchbar/SearchBar'
import AntdTable from '@/components/table/AntdTable'
import { SPECIAL_TYPE_RECORD_STATUS } from '@/utils/types'

const statusObj = initCurryingObj(SPECIAL_TYPE_RECORD_STATUS)
const menuPerm = 'menu_rfid_configuration_specialSku'

/** 产品代码：6 位字符 + "-" + 3 位数字，大小写不敏感 */
const PRODUCT_CODE_PATTERN = /^[A-Za-z0-9]{6}-\d{3}$/

const SpecialSkuConfigPage = () => {
  const [newModalForm] = Form.useForm()
  const [tableList, setTableList] = useState([])
  const [queryFlag, setQueryFlag] = useState(false)
  const [pagination, setPagination] = useState(paginationInit)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [plantList, setPlantList] = useState([])
  const [visibleFlag, setVisibleFlag] = useState(false)
  const mounting = useRef(false)
  const formRef = useRef(null)
  const editItemInfo = useRef({})

  const searchListInit = [
    {
      type: 'input',
      label: 'languageObj.specialSkuConfig.productCode',
      key: 'productCode',
      allowClear: true,
      initialValues: '',
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialSkuConfig.plant',
      key: 'plantList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.specialCode',
      key: 'specialCodeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.specialType',
      key: 'specialTypeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.exceptionType',
      key: 'exceptionTypeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.commonLocale.status',
      key: 'status',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: SPECIAL_TYPE_RECORD_STATUS,
      spans: { xl: 8, xxl: 8 }
    }
  ]
  const [searchList, setSearchList] = useState(searchListInit)
  const [specialTypeList, setSpecialTypeList] = useState([])
  const modalProductName = Form.useWatch('productName', newModalForm)

  const addAndEditRecord = record => {
    setVisibleFlag(true)
    if (record && record.id) {
      editItemInfo.current = record
      newModalForm.setFieldsValue({
        productCode: record.productCode,
        productName: record.productName || '',
        plant: record.plant,
        specialCode: record.specialCode
      })
    } else {
      editItemInfo.current = {}
      newModalForm.resetFields()
    }
  }

  const tableColumns = [
    {
      title: formatMessageNew('languageObj.specialSkuConfig.productCode'),
      dataIndex: 'productCode',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.productName'),
      dataIndex: 'productName',
      width: 150
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.plant'),
      dataIndex: 'plant',
      width: 100
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.specialCode'),
      dataIndex: 'specialCode',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.specialType'),
      dataIndex: 'specialType',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.exceptionType'),
      dataIndex: 'exceptionTypeName',
      width: 150
    },
    {
      title: formatMessageNew('languageObj.commonLocale.status'),
      dataIndex: 'status',
      width: 100,
      render: text => statusObj[text]
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.createTime'),
      dataIndex: 'createTime',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.lastUpdatedBy'),
      dataIndex: 'updateBy',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.specialSkuConfig.lastUpdateTime'),
      dataIndex: 'updateTime',
      width: 120
    },
    {
      title: formatMessageNew('languageObj.commonLocale.operation'),
      dataIndex: 'operation',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <div>
          <Button
            disabled={!hasPermission(menuPerm) || record.status === 1}
            type="link"
            size="small"
            onClick={() => addAndEditRecord(record)}
          >
            {formatMessageNew('languageObj.commonLocale.edit')}
          </Button>
        </div>
      )
    }
  ]

  useEffect(() => {
    mounting.current && searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])

  useEffect(() => {
    getSelectOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSelectOptions = () => {
    Promise.all([getExceptionTypeList(), findSpecialCodeAndType(), getPlantList()]).then(res => {
      const next = [...searchListInit]
      const extRaw = res[0].resultData
      const extList = Array.isArray(extRaw) ? extRaw : []
      const rawDrop = res[1].resultData
      const resDt = Array.isArray(rawDrop) ? rawDrop : []
      const rawPlantDrop = res[2].resultData
      const resPlantDt = Array.isArray(rawPlantDrop) ? rawPlantDrop : []
      /** 搜索条件和新增弹窗的 Plant 共用同一份下拉数据 */
      const plantOptions = initValueAndLabel(uniqueFun(resPlantDt, 2))
      setPlantList(plantOptions)
      /** Special Type 配置中启用的记录，用于下拉展示「Special Code – Special Type」 */
      let list = resDt.map(row => {
        const code = row.specialCode
        const st = row.specialType
        return {
          value: code,
          label: `${code} – ${st}`
        }
      })
      setSpecialTypeList(list)
      next.forEach(item => {
        if (item.key === 'exceptionTypeList') {
          item.selectList = initValueAndLabel(uniqueFun(extList, 2), {
            value: 'code',
            label: 'name'
          })
        }
        if (item.key === 'specialCodeList') {
          const arr = resDt.map(row => row.specialCode) || []
          item.selectList = initValueAndLabel(uniqueFun(arr, 2))
        }
        if (item.key === 'specialTypeList') {
          const arr = resDt.map(row => row.specialType) || []
          item.selectList = initValueAndLabel(uniqueFun(arr, 2))
        }
        if (item.key === 'plantList') {
          item.selectList = plantOptions
        }
      })
      setSearchList(next)
    })
  }

  const getParams = () => {
    const info = (formRef.current && formRef.current.getSearchValue()) || {}
    let status = ''
    if (info.status && info.status.length === 1) {
      status = info.status[0]
    }
    return { ...info, status }
  }

  const downLoadFun = () => {
    downloadData({ ...getParams() }).then(res => {
      downLoadFileBlob(res)
    })
  }

  const searchDataBtn = () => {
    mounting.current = true
    const pg = { ...pagination, currentPage: 1 }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const searchClearDataBtn = () => {
    mounting.current = true
    formRef.current && formRef.current.clearForm()
    setSelectedRowKeys([])
    const pg = { ...pagination, currentPage: 1 }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const handleTableChange = pagina => {
    const params = {
      ...pagination,
      currentPage: pagina.current,
      pageSize: pagina.pageSize
    }
    setSelectedRowKeys([])
    setPagination({ ...params })
    setQueryFlag(!queryFlag)
  }

  const searchDataFun = () => {
    const params = {
      pageNo: pagination.currentPage,
      size: pagination.pageSize,
      ...getParams()
    }
    getTableData(params).then(res => {
      const dt = res.resultData
      setTableList(dt.records || [])
      setPagination(p => ({
        ...p,
        total: dt.total
      }))
    })
  }

  const modalErrorFun = error => {
    const code = error.errorCode
    const msg = error.errorMessage
    const str = formatMessageNew(`languageObj.specialSkuConfig.errorcode_${code}`) || msg
    modalError({
      content: str
    })
  }

  const fetchProductName = () => {
    const code = (newModalForm.getFieldValue('productCode') || '').trim()
    if (!code) return
    if (!PRODUCT_CODE_PATTERN.test(code)) {
      modalErrorFun({
        errorCode: 'invalid_format'
      })
      newModalForm.setFieldsValue({ productName: '' })
      return
    }
    queryProductByCode({ productCode: code })
      .then(res => {
        const productName = res.resultData
        newModalForm.setFieldsValue({ productName: productName })
        if (!productName) {
          modalErrorFun({
            errorCode: '436'
          })
        }
      })
      .catch(err => {
        modalErrorFun(err)
        newModalForm.setFieldsValue({ productName: '' })
      })
  }

  const doDisable = () => {
    if (selectedRowKeys.length === 0) {
      modalError({ content: formatMessageNew('languageObj.specialTypeConfig.selectData') })
      return
    }
    disableRecords(selectedRowKeys[0]).then(() => {
      messageModal('success', {
        content: formatMessageNew('languageObj.commonLocale.success')
      })
      setSelectedRowKeys([])
      setQueryFlag(!queryFlag)
    })
  }

  const disabledDataFun = () => {
    if (selectedRowKeys.length === 0) {
      modalError({ content: formatMessageNew('languageObj.specialTypeConfig.selectData') })
      return
    }
    modalConfirm({ content: formatMessageNew('languageObj.specialSkuConfig.disableConfirm') }, doDisable)
  }

  const handleOk = () => {
    newModalForm.validateFields().then(values => {
      const productCode = (values.productCode || '').trim()
      if (!editItemInfo.current.id && !PRODUCT_CODE_PATTERN.test(productCode)) {
        modalErrorFun({
          errorCode: 'invalid_format'
        })
        return
      }
      if (editItemInfo.current.id) {
        const params = {
          id: editItemInfo.current.id,
          plant: values.plant,
          productCode: (values.productCode || '').trim(),
          specialCode: values.specialCode
        }
        updateData(params)
          .then(() => {
            messageModal('success', {
              content: formatMessageNew('languageObj.commonLocale.success')
            })
            setQueryFlag(!queryFlag)
            setVisibleFlag(false)
          })
          .catch(err => {
            modalErrorFun(err)
          })
      } else {
        const params = {
          plant: values.plant,
          productCode,
          specialCode: values.specialCode
        }
        addData(params)
          .then(() => {
            messageModal('success', {
              content: formatMessageNew('languageObj.commonLocale.success')
            })
            setQueryFlag(!queryFlag)
            setVisibleFlag(false)
          })
          .catch(err => {
            modalErrorFun(err)
          })
      }
    })
  }

  const handleCancel = () => {
    setVisibleFlag(false)
  }

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    fixed: 'left',
    columnWidth: 50,
    onChange: keys => setSelectedRowKeys(keys),
    getCheckboxProps: record => ({
      disabled: record.status === 1
    })
  }

  const hasSelected = selectedRowKeys.length > 0

  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar
            ref={formRef}
            searchList={searchList}
            showCollapseBtn={false}
            showSearchBtn={true}
            showClearBtn={true}
            onSearch={searchDataBtn}
            onClear={searchClearDataBtn}
          />
        </div>
        <div className="displayFlex alignCenter justifyContentSB">
          {hasPermission(menuPerm) ? (
            <div className="displayFlex alignCenter">
              <Button type="primary" className="ml16" onClick={() => addAndEditRecord()}>
                <PlusOutlined />
                {formatMessageNew('languageObj.commonLocale.addNew')}
              </Button>
              <Button type="primary" danger disabled={!hasSelected} className="ml20" onClick={() => disabledDataFun()}>
                {formatMessageNew('languageObj.commonLocale.disable')}
              </Button>
            </div>
          ) : (
            <div className="auto-mt20"></div>
          )}
        </div>
        <div className="content-container overflow_visible">
          <AntdTable
            rowKey={record => record.id}
            dataSource={tableList}
            onDownload={downLoadFun}
            columns={tableColumns}
            pagination={pagination}
            onChange={handleTableChange}
            rowSelection={hasPermission(menuPerm) && rowSelection}
            // 独一无二的标识，不能重复
            columnsKey="columns_specialSkuConfig"
          />
        </div>
      </Card>
      <Modal
        open={visibleFlag}
        maskClosable={false}
        className="nike_modal"
        width="48%"
        title={
          editItemInfo.current.id
            ? formatMessageNew('languageObj.commonLocale.edit')
            : formatMessageNew('languageObj.commonLocale.addNew')
        }
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={newModalForm} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} initialValues={{}}>
          <Form.Item name="productName" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="productCode"
            label={formatMessageNew('languageObj.specialSkuConfig.productCode')}
            rules={
              editItemInfo.current.id
                ? []
                : [{ required: true, message: formatMessageNew('languageObj.commonLocale.placeholderInput') }]
            }
            extra={
              <div style={{ marginTop: 4 }}>
                {modalProductName ? (
                  <Typography.Text style={{ color: '#1677ff', display: 'block' }}>{modalProductName}</Typography.Text>
                ) : null}
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {formatMessageNew('languageObj.specialSkuConfig.productCodeEnterHint')}
                </Typography.Text>
              </div>
            }
          >
            <Input
              disabled={!!editItemInfo.current.id}
              placeholder={formatMessageNew('languageObj.commonLocale.placeholderInput')}
              onPressEnter={fetchProductName}
            />
          </Form.Item>
          <Form.Item
            name="plant"
            label={formatMessageNew('languageObj.specialSkuConfig.plant')}
            rules={[{ required: true, message: formatMessageNew('languageObj.commonLocale.placeholderSelect') }]}
          >
            <Select
              allowClear
              showSearch
              className="w100"
              placeholder={formatMessageNew('languageObj.commonLocale.placeholderSelect')}
              options={plantList}
            />
          </Form.Item>
          <Form.Item
            name="specialCode"
            label={formatMessageNew('languageObj.specialSkuConfig.specialCodeSelectLabel')}
            rules={[
              {
                required: true,
                message: `${formatMessageNew('languageObj.commonLocale.placeholderSelect')} ${formatMessageNew(
                  'languageObj.specialSkuConfig.specialCodeSelectLabel'
                )}`
              }
            ]}
          >
            <Select
              allowClear
              showSearch
              className="w100"
              placeholder={formatMessageNew('languageObj.commonLocale.placeholderSelect')}
              options={specialTypeList}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SpecialSkuConfigPage
